import { cn } from '@/lib/utils'

import { hourlyCapStatusColor, hourlyCapStatusTextClass } from '../constants'
import type { AllocationDuty } from '../types'

/**
 * The "Hourly limit" cell: a thin proportional bar of `calls_this_hour` against
 * the duty's `hourly_cap`, with an `X/cap` label to its right. The fill ramps
 * green → amber → red as utilization nears the cap (dataviz status palette); the
 * width clamps at 100% so an over-cap value fills the track instead of
 * overflowing. Width eases on polls (motion-safe), mirroring the ribbon. Exposed
 * as an ARIA progressbar so the raw value/max reach assistive tech and tests.
 *
 * Three states:
 * - no limit configured (`hourly_cap == null`) — a muted dash.
 * - a limit, but no usage (`calls_this_hour == null`) — the cap on its own, muted.
 *   This is the live state today: counting calls-this-hour costs a full table scan
 *   per poll, so the backend defers it and sends null while still sending the cap.
 *   Showing "500/hr" beats a bare dash — the configured limit is real information,
 *   and a column of dashes reads as broken rather than pending. Rendering usage as
 *   zero would be worse still: unknown is not none.
 * - both present — the bar below. Kept intact so the column lights back up on a
 *   backend-only change, with no work here.
 */
export function HourlyLimitBar({ duty }: { duty: AllocationDuty }) {
  const { hourly_cap: cap, calls_this_hour: used } = duty
  if (cap == null) {
    return <span className="text-muted-foreground">—</span>
  }
  if (used == null) {
    return (
      <span
        className="text-xs tabular-nums text-muted-foreground"
        title="Calls this hour aren't reported yet; showing the configured limit.">
        {cap.toLocaleString()}/hr
      </span>
    )
  }

  const ratio = cap > 0 ? used / cap : 0
  const pct = Math.min(100, ratio * 100)
  const utilization = ratio * 100
  return (
    <div className="flex items-center gap-2">
      <div
        role="progressbar"
        aria-label={`${used.toLocaleString()} / ${cap.toLocaleString()} calls this hour`}
        // ARIA requires valuenow within [min, max]; behaviour above max is
        // undefined, so a screen reader might announce ">100%" or clamp silently —
        // making the over-cap case, the one that matters most, the least reliably
        // conveyed. Clamp the numeric value and put the truth in valuetext.
        aria-valuenow={Math.min(used, cap)}
        aria-valuemin={0}
        aria-valuemax={cap}
        aria-valuetext={
          used > cap
            ? `${used.toLocaleString()} of ${cap.toLocaleString()} calls this hour — over the limit`
            : `${used.toLocaleString()} of ${cap.toLocaleString()} calls this hour`
        }
        className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full motion-safe:transition-[width]"
          style={{
            width: `${pct}%`,
            backgroundColor: hourlyCapStatusColor(utilization),
          }}
        />
      </div>
      <span
        className={cn(
          'shrink-0 text-xs font-medium tabular-nums',
          hourlyCapStatusTextClass(utilization)
        )}>
        {used.toLocaleString()}/{cap.toLocaleString()}
      </span>
    </div>
  )
}
